import { KeyEvent } from 'library/ts/ui/events/KeyEvent';
import { TrueType } from 'library/ts/utilities/modules/enums/TrueType';
import * as Type from 'library/ts/utilities/modules/Type';
const isObjectEqual = (objectA, objectB) => {
    const aKeys = Object.keys(objectA);
    if (aKeys.length !== Object.keys(objectB).length) {
        return false;
    }
    return aKeys.every((key) => (Object.prototype.hasOwnProperty.call(objectB, key)
        && objectA[key] === objectB[key]));
};
const isArrayEqual = (a, b) => a.length === b.length
    && a.every((v, i) => isObjectEqual(v, b[i]));
export const matchHotkey = (buffer, hotkey) => {
    if (buffer.length < hotkey.length) {
        return false;
    }
    const indexDiff = buffer.length - hotkey.length;
    for (let i = hotkey.length - 1; i >= 0; i -= 1) {
        if (!isObjectEqual(buffer[indexDiff + i], hotkey[i])) {
            return false;
        }
    }
    return true;
};
const arrayToObject = (array) => (array.reduce((obj, key) => ({ ...obj, [key]: true }), {}));
const allModifiers = ['ctrl', 'shift', 'alt', 'meta'];
const indexedModifiers = arrayToObject(allModifiers);
const isHotkeyValid = (hotkey) => Object.keys(hotkey).filter((key) => !indexedModifiers[key]).length === 1;
const validate = (value, message) => {
    if (!value) {
        throw new Error(message);
    }
};
const validateType = (value, name, type) => {
    validate(typeof value === type, `The ${name} must be a ${type}; given ${typeof value}`);
};
export const normalizeHotkey = (hotkey) => (hotkey.split(/ +/g).map((part) => {
    const array = part.split('+').filter(Boolean);
    const result = arrayToObject(array);
    validate(Object.keys(result).length >= array.length, `Hotkey combination has duplicates "${hotkey}"`);
    validate(isHotkeyValid(result), `Invalid hotkey combination: "${hotkey}"`);
    return result;
}));
const validateListenerArguments = (hotkey, callback) => {
    validateType(hotkey, 'hotkey', TrueType.STRING);
    validateType(callback, 'callback', TrueType.FUNCTION);
};
const createListenersFunction = (listeners, listenerFunction) => ((hotkey, callback) => {
    validateListenerArguments(hotkey, callback);
    listenerFunction(listeners, hotkey, callback);
});
const registerListener = (listeners, hotkey, callback) => {
    listeners.push({ hotkey: normalizeHotkey(hotkey), callback });
};
const unregisterListener = (listeners, hotkey, callback) => {
    const normalized = normalizeHotkey(hotkey);
    const index = listeners.findIndex((listener) => listener.callback === callback
        && isArrayEqual(normalized, listener.hotkey));
    if (index !== -1) {
        listeners.splice(index, 1);
    }
};
const debounce = (debounceFunction, debounceTime) => {
    let timeoutId = null;
    return () => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(debounceFunction, debounceTime);
    };
};
const getKey = (key) => {
    switch (key) {
        case '+':
            return 'plus';
        case ' ':
            return 'space';
        default:
            return key;
    }
};
const createKeyDownListener = (listeners, debounceTime) => {
    let buffer = [];
    const clearBufferDebounced = debounce(() => {
        buffer = [];
    }, debounceTime);
    return (event) => {
        if (event.repeat) {
            return;
        }
        if (Type.isFunction(event?.getModifierState) && event.getModifierState(event.key)) {
            return;
        }
        clearBufferDebounced();
        const description = {
            [getKey(event.key)]: true,
        };
        allModifiers.forEach((modifier) => {
            if (event[`${modifier}Key`]) {
                description[modifier] = true;
            }
        });
        buffer.push(description);
        listeners.forEach((listener) => {
            if (matchHotkey(buffer, listener.hotkey)) {
                listener.callback(event);
            }
        });
    };
};
const validateContext = (options) => {
    validateType(options.debounceTime, 'debounceTime', TrueType.NUMBER);
    validate(options.debounceTime > 0, 'debounceTime must be > 0');
    validateType(options.autoEnable, 'autoEnable', TrueType.BOOLEAN);
    return options;
};
export const createContext = (options = { autoEnable: true, debounceTime: 500 }) => {
    options = validateContext(options);
    const listeners = [];
    const keyDownListener = createKeyDownListener(listeners, options.debounceTime);
    const enable = () => document.addEventListener(KeyEvent.KEYDOWN, keyDownListener);
    const disable = () => document.removeEventListener(KeyEvent.KEYDOWN, keyDownListener);
    if (options.autoEnable) {
        enable();
    }
    return {
        register: createListenersFunction(listeners, registerListener),
        unregister: createListenersFunction(listeners, unregisterListener),
        enable,
        disable,
    };
};
